import { useEffect, useMemo, useState } from "react";
import { Bar, BarChart, CartesianGrid, LabelList, XAxis, YAxis } from "recharts";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "../ui/chart";
import { formatInvoiceAmount, formatServiceMonthLabel } from "../../utils/dashboardRevenue";

const CHART_CONFIG = {
  revenue: {
    label: "Revenue",
    color: "#1a3268",
  },
};

function formatCompactCurrency(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return "$0";
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(numeric);
}

const BAR_ANIMATION_DURATION_MS = 2000;
const BAR_STAGGER_MS = 220;
const BAR_ANIMATION_START_DELAY_MS = 120;

function zeroedMonthlyData(data) {
  return data.map((row) => ({ ...row, revenue: 0 }));
}

function easeOutCubic(progress) {
  return 1 - Math.pow(1 - progress, 3);
}

function buildAnimatedChartData(monthlyData, elapsedMs) {
  return monthlyData.map((row, index) => {
    const barStart = BAR_ANIMATION_START_DELAY_MS + index * BAR_STAGGER_MS;
    const barElapsed = elapsedMs - barStart;
    const progress = Math.min(Math.max(barElapsed / BAR_ANIMATION_DURATION_MS, 0), 1);
    return {
      ...row,
      revenue: row.revenue * easeOutCubic(progress),
    };
  });
}

function YtdMonthlyRevenueChartDialog({ open, onOpenChange, monthlyData, year, scopeLabel }) {
  const [chartData, setChartData] = useState(() => zeroedMonthlyData(monthlyData));

  const hasData = monthlyData.some((row) => row.revenue > 0);

  const yAxisMax = useMemo(() => {
    const max = Math.max(0, ...monthlyData.map((row) => row.revenue));
    return max > 0 ? Math.ceil(max * 1.15) : 1;
  }, [monthlyData]);

  useEffect(() => {
    if (!open) {
      setChartData(zeroedMonthlyData(monthlyData));
      return undefined;
    }

    const totalDuration =
      BAR_ANIMATION_START_DELAY_MS +
      Math.max(0, monthlyData.length - 1) * BAR_STAGGER_MS +
      BAR_ANIMATION_DURATION_MS;
    const animationStart = performance.now();
    let frameId = 0;

    const tick = (now) => {
      const elapsed = now - animationStart;
      setChartData(buildAnimatedChartData(monthlyData, elapsed));

      if (elapsed < totalDuration) {
        frameId = window.requestAnimationFrame(tick);
      } else {
        setChartData(monthlyData);
      }
    };

    setChartData(zeroedMonthlyData(monthlyData));
    frameId = window.requestAnimationFrame(tick);

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [open, monthlyData]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gap-6 sm:max-w-2xl" closeLabel="Close">
        <DialogHeader>
          <DialogTitle>Monthly revenue — {year}</DialogTitle>
          <DialogDescription>
            Recognized (non-draft) revenue by service month
            {scopeLabel ? ` · ${scopeLabel}` : ""}.
          </DialogDescription>
        </DialogHeader>

        {!hasData ? (
          <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
            No recognized revenue recorded for this period.
          </p>
        ) : (
          <ChartContainer config={CHART_CONFIG} className="aspect-[16/9] w-full min-h-[280px]">
            <BarChart data={chartData} margin={{ top: 28, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid vertical={false} strokeDasharray="3 3" className="stroke-gray-200/80 dark:stroke-gray-700/80" />
              <XAxis
                dataKey="label"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                className="fill-gray-600 dark:fill-gray-400"
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                width={72}
                domain={[0, yAxisMax]}
                tickFormatter={formatCompactCurrency}
                className="fill-gray-600 dark:fill-gray-400"
              />
              <ChartTooltip
                cursor={{ fill: "rgba(26, 50, 104, 0.08)" }}
                content={
                  <ChartTooltipContent
                    formatter={(value) => formatInvoiceAmount(value)}
                    labelFormatter={(_label, payload) => {
                      const row = payload?.[0]?.payload;
                      return row?.monthKey ? formatServiceMonthLabel(row.monthKey) : _label;
                    }}
                  />
                }
              />
              <Bar
                dataKey="revenue"
                fill="var(--color-revenue)"
                radius={[6, 6, 0, 0]}
                maxBarSize={48}
                isAnimationActive={false}
              >
                <LabelList
                  dataKey="revenue"
                  position="top"
                  offset={6}
                  formatter={(value) =>
                    Number(value) > 0 ? formatCompactCurrency(value) : ""
                  }
                  className="fill-gray-700 dark:fill-gray-300"
                  fontSize={11}
                  fontWeight={500}
                />
              </Bar>
            </BarChart>
          </ChartContainer>
        )}
      </DialogContent>
    </Dialog>
  );
}

export { YtdMonthlyRevenueChartDialog };
